import React from 'react';

import {
    Button,
    TextField,
    MenuItem,
    IconButton,
    Box,
    Typography,
    Divider
} from '@mui/material';

import * as ETVConstants from '../../ETVConstants';
import { Add, Delete, Dialpad } from '@mui/icons-material';
import ETVPopup from '../../tools/ETVPopup';
import ETVActionButton from '../../tools/ETVActionButton';
import ETVMessagePopup from '../../tools/ETVMessagePopup';

const FIXED_ALARM_COMMANDS = [
    {
        key: 1,
        title: 'Alarm acknowledged',
        description: 'The emergency operator confirms that the alarm has been received.'
    },
    {
        key: 2,
        title: 'Help is on the way',
        description: 'The emergency operator confirms that assistance has been dispatched.'
    },
    {
        key: 3,
        title: 'Situation resolved',
        description: 'Ends the active alarm, including a confirmed false alarm.'
    }
];

const MIN_CUSTOM_DTMF_KEY = 4;

class IntercomDTMFDialog extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            saving: false,
            uploading: false,
            items: [],
            errors: {},
            errorOpen: false,
            errorMessage: '',
            errorDetails: ''
        };
    }

    componentDidMount() {
        if (this.props.open) {
            this.loadDTMF();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.loadDTMF();
        }
    }

    getCredentials = () => {
        return {
            accessToken: localStorage.getItem('etv.access_token'),
            reqUserID: localStorage.getItem('etv.user_id')
        };
    };

    getAuthHeaders = () => {
        const { accessToken, reqUserID } = this.getCredentials();

        return {
            user_id: reqUserID,
            access_token: accessToken
        };
    };

    loadDTMF = async () => {
        const { accessToken, reqUserID } = this.getCredentials();
        if (!reqUserID || !accessToken) {
            console.error('Missing user_id or access_token');
            return;
        }
        this.setState({ loading: true });
        try {
            const url = ETVConstants.getServerBase() + '/intercom_dtmf?action=list_dtmf&user_id=' + encodeURIComponent(reqUserID);
            const response = await fetch(url, {
                headers: this.getAuthHeaders()
            });
            const json = await response.json();
            if (!json.result) {
                console.error('DTMF load failed:', json.data);
                this.setState({ items: [] });
                return;
            }
            const list = Array.isArray(json.list) ? json.list : [];
            this.setState({
                items: list
                    .map(item => ({
                        account_id: item.account_id,
                        dtmf_key: Number(item.dtmf_key || 0),
                        dtmf_action: item.dtmf_action || 'message',
                        audio_url: item.audio_url || '',
                        media_url: item.media_url || '',
                        value: item.value || ''
                    }))
                    .filter(item => item.dtmf_key >= MIN_CUSTOM_DTMF_KEY)
                    .sort((a, b) => a.dtmf_key - b.dtmf_key)
            });
        } catch (e) {
            console.error('DTMF load failed', e);
            this.setState({ items: [] });
        } finally {
            this.setState({ loading: false });
        }
    };

    addItem = () => {
        this.setState(prev => {
            const usedKeys = new Set(prev.items.map(item => Number(item.dtmf_key)));
            let nextKey = MIN_CUSTOM_DTMF_KEY;
            while (usedKeys.has(nextKey)) nextKey += 1;
            return {
                items: [
                    ...prev.items,
                    {
                        account_id: null,
                        dtmf_key: nextKey,
                        dtmf_action: 'message',
                        value: ''
                    }
                ]
            };
        });
    };

    removeItem = async index => {
        const item = this.state.items[index];
        // If it is only a new unsaved row, remove locally
        if (!item || !item.dtmf_key) {
            this.setState(prev => ({
                items: prev.items.filter((_, i) => i !== index)
            }));
            return;
        }
        this.setState({ saving: true });
        try {
            await this.deleteItem(item);
            this.setState(prev => ({
                items: prev.items.filter((_, i) => i !== index)
            }));
        } catch (e) {
            console.error('DTMF delete failed', e);
        } finally {
            this.setState({ saving: false });
        }
    };

    validateItems = () => {
        const errors = {};
        const usedKeys = new Set();
        this.state.items.forEach((item, index) => {
            errors[index] = {};
            if (!Number.isInteger(item.dtmf_key) || item.dtmf_key < MIN_CUSTOM_DTMF_KEY) {
                errors[index].dtmf_key = 'Custom commands start at 4#';
            }
            if (usedKeys.has(item.dtmf_key)) {
                errors[index].dtmf_key = ETVConstants.trans("must_be_unique");
            }
            usedKeys.add(item.dtmf_key);
            if (!item.dtmf_action) {
                errors[index].dtmf_action = ETVConstants.trans("required");
            }
            if (!item.value || item.value.trim() === '') {
                errors[index].value = ETVConstants.trans("required");
            }
        });

        const hasErrors =
            Object.values(errors).some(
                row => Object.keys(row).length > 0
            );
        this.setState({ errors });
        return !hasErrors;
    };   

    updateItem = (index, field, value) => {
        if (this.state.uploading) return;
        this.setState(prev => {
            const items = [...prev.items];
            items[index] = {
                ...items[index],
                [field]: field === 'dtmf_key' ? Number(value) : value
            };
            if (field === 'dtmf_action') { items[index].value = ''; items[index].audio_url = ''; items[index].media_url = ''; items[index].filename = ''; }
            return { items };
        });
    };

    saveItem = async item => {
        const { reqUserID } = this.getCredentials();
        const url =
            ETVConstants.getServerBase() +
            '/intercom_dtmf?action=set_dtmf' +
            '&account_id=' + encodeURIComponent(reqUserID) +
            '&dtmf_key=' + encodeURIComponent(item.dtmf_key) +
            '&dtmf_action=' + encodeURIComponent(item.dtmf_action) +
            '&value=' + encodeURIComponent(item.value);

        const response = await fetch(url, {
            headers: this.getAuthHeaders()
        });
        const responseText = await response.text();
        let json;
        try {
            json = JSON.parse(responseText);
        } catch (error) {
            throw new Error(
                `Server returned HTTP ${response.status} with an invalid response: ${responseText || 'empty response'}`
            );
        }
        if (!response.ok || !json.result) {
            throw new Error(
                json.reason || json.error || json.data ||
                `Failed to save DTMF command ${item.dtmf_key}# (HTTP ${response.status})`
            );
        }
        return json;
    };

    saveAll = async () => {
        if (this.state.uploading) return;
        if (!this.validateItems()) {
            return;
        }
        const { accessToken, reqUserID } = this.getCredentials();
        if (!reqUserID || !accessToken) {
            console.error('Missing user_id or access_token');
            return;
        }
        this.setState({ saving: true });
        try {
            for (const item of this.state.items) {
                await this.saveItem(item);
            }
            await this.loadDTMF();
            if (this.props.onClose) {
                this.props.onClose();
            }
        } catch (e) {
            console.error('DTMF save failed', e);
            this.setState({
                errorOpen: true,
                errorMessage: 'Failed to save DTMF settings',
                errorDetails: String(e?.message || e)
            });
        } finally {
            this.setState({ saving: false });
        }
    };

    uploadAudio = async (index, file) => {
        if (!file) return;
        this.setState({ uploading: true });
        try {
            const kind = this.state.items[index].dtmf_action;
            const extensions = {audio: /\.mp3$/i, image: /\.(png|jpe?g)$/i, video: /\.mp4$/i};
            const limit = kind === 'video' ? 100 : 10;
            if (!extensions[kind]?.test(file.name) || !file.size || file.size > limit * 1024 * 1024)
                throw new Error(`Choose a valid ${kind} file up to ${limit} MB.`);
            const body = new FormData();
            body.append('file', file);
            const response = await fetch(ETVConstants.getServerBase() + '/intercom_dtmf?action=' + (kind === 'audio' ? 'upload_audio' : 'upload_media&kind=' + kind), {
                method: 'POST', headers: this.getAuthHeaders(), body
            });
            const result = await response.json();
            if (!response.ok || !result.result) throw new Error(result.reason || result.data || 'Media upload failed');
            this.setState(prev => ({ items: prev.items.map((item, i) => i === index ? {
                ...item, value: result.value, audio_url: result.audio_url, media_url: result.media_url, filename: file.name
            } : item) }));
        } catch (error) {
            this.setState({ errorOpen: true, errorMessage: 'Media upload failed', errorDetails: String(error.message || error) });
        } finally { this.setState({ uploading: false }); }
    };

    renderItem = (item, index) => {
        const error = this.state.errors[index] || {};
        const media = {
            audio: {label: 'MP3', accept: '.mp3,audio/mpeg', help: 'MP3 - maximum 10 MB / 5 minutes'},
            image: {label: 'image', accept: '.jpg,.jpeg,.png,image/jpeg,image/png', help: 'JPEG or PNG - maximum 10 MB / 40 megapixels'},
            video: {label: 'MP4', accept: '.mp4,video/mp4', help: 'MP4 (H.264 / AAC recommended) - maximum 100 MB'}
        }[item.dtmf_action];
        return (
            <div
                key={index}
                style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    marginBottom: 14,
                    marginTop:10
                }}
            >

                <TextField
                    label="DTMF key"
                    type="number"
                    value={item.dtmf_key}
                    error={!!error.dtmf_key}
                    helperText={error.dtmf_key}
                    onChange={e =>
                        this.updateItem(index,'dtmf_key',e.target.value)
                    }
                    inputProps={{ min: MIN_CUSTOM_DTMF_KEY, step: 1 }}
                    sx={{
                        width:80,
                        minWidth:80
                    }}
                />

                <TextField
                    select
                    disabled={this.state.uploading || this.state.saving}
                    label="Action"
                    value={item.dtmf_action}
                    error={!!error.dtmf_action}
                    helperText={error.dtmf_action}
                    onChange={e =>
                        this.updateItem(index,'dtmf_action',e.target.value)
                    }
                    sx={{
                        width:190,
                        minWidth:190
                    }}
                >
                    <MenuItem value="message"> {ETVConstants.trans("message")} </MenuItem>
                    <MenuItem value="audio">Play audio (MP3)</MenuItem>
                    <MenuItem value="image">Show image</MenuItem>
                    <MenuItem value="video">Play video (MP4)</MenuItem>
                </TextField>

                {media ? <Box sx={{flex: 1, minWidth: 0}}>
                    <Button component="label" disabled={this.state.uploading || this.state.saving}>
                        {this.state.uploading ? 'Uploading...' : item.value ? `Replace ${media.label}` : `Upload ${media.label}`}
                        <input hidden type="file" accept={media.accept} onChange={e => {
                            this.uploadAudio(index, e.target.files?.[0]); e.target.value = '';
                        }}/>
                    </Button>
                    <Typography variant="caption" display="block">{item.filename || (item.value ? `Uploaded ${media.label}` : media.help)}</Typography>
                    {item.audio_url && <audio controls preload="none" src={item.audio_url} style={{width: '100%'}}/>}
                    {item.dtmf_action === 'image' && item.media_url && <img src={item.media_url} alt="DTMF command preview" style={{maxWidth: '100%', maxHeight: 160, objectFit: 'contain'}}/>}
                    {item.dtmf_action === 'video' && item.media_url && <video controls preload="metadata" src={item.media_url} style={{width: '100%', maxHeight: 180}}/>}
                    {error.value && <Typography variant="caption" color="error">Upload a {media.label} file</Typography>}
                </Box> : <TextField
                    label="Value"
                    value={item.value}
                    error={!!error.value}
                    helperText={error.value}
                    onChange={e =>
                        this.updateItem(index,'value',e.target.value)
                    }
                    sx={{
                        flex:1
                    }}
                />}

				<IconButton disabled={this.state.uploading || this.state.saving} color="error" aria-label={ETVConstants.trans("delete") || "Delete DTMF command"} onClick={() => this.removeItem(index)} sx={{bgcolor:'rgba(211,47,47,.08)','&:hover':{bgcolor:'rgba(211,47,47,.14)'}}}>
                    <Delete />
                </IconButton>
            </div>
        );
    };

    deleteItem = async item => {
        const { reqUserID } = this.getCredentials();
        const url =
            ETVConstants.getServerBase() +
            '/intercom_dtmf?action=delete_dtmf' +
            '&account_id=' + encodeURIComponent(reqUserID) +
            '&dtmf_key=' + encodeURIComponent(item.dtmf_key);

        const response = await fetch(url, {
            headers: this.getAuthHeaders()
        });
        const json = await response.json();
        if (!json.result) {
            throw new Error(json.data || 'Failed to delete DTMF item');
        }
        return json;
    };


    render() {
        const { open, onClose } = this.props;
        const { loading, saving, items, errorOpen, errorMessage, errorDetails } = this.state;
        return (
			<>
			<ETVPopup
                open={open}
                onClose={onClose}
				width="standard"
				icon={<Dialpad/>}
				title={ETVConstants.trans("dtmf_settings")}
				description={ETVConstants.trans("dtmf_commands")}
				loading={loading || saving}
					topActions={
					<Button variant="outlined" startIcon={<Add/>} onClick={this.addItem} disabled={loading || saving || this.state.uploading}>
						{ETVConstants.trans("add_custom_dtmf") || 'Add custom DTMF action'}
					</Button>
				}
				bottomActions={<>
					<ETVActionButton action="cancel" onClick={onClose} disabled={saving || this.state.uploading}/>
					<ETVActionButton action="save" onClick={this.saveAll} loading={saving} disabled={loading || this.state.uploading}/>
				</>}
            >
				{!loading && (
                    <>
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            Alarm workflow commands
                        </Typography>
                        <Box sx={{display: 'grid', gap: 1}}>
                            {FIXED_ALARM_COMMANDS.map(command => (
                                <Box
                                    key={command.key}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '48px 1fr',
                                        gap: 1.5,
                                        alignItems: 'center',
                                        p: 1.25,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography variant="h6" color="primary" sx={{fontWeight: 700}}>
                                        {command.key}#
                                    </Typography>
                                    <Box>
                                        <Typography variant="body2" sx={{fontWeight: 600}}>
                                            {command.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {command.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Divider sx={{my: 2}} />

                        <Typography variant="subtitle2">
                            Custom commands (4# and above)
                        </Typography>
                        {items.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                No custom DTMF commands configured.
                            </Typography>
                        )}
                        {items.map((item, index) => this.renderItem(item, index))}
                    </>
                )}
			</ETVPopup>
			<ETVMessagePopup
				open={errorOpen}
				type="error"
				title="DTMF settings"
				message={errorMessage}
				details={errorDetails}
				onClose={() => this.setState({
					errorOpen: false,
					errorMessage: '',
					errorDetails: ''
				})}
			/>
			</>
        );
    }
}

export default IntercomDTMFDialog;
